import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, RefreshCw, Loader2, Check, AlertTriangle,
  Activity, FileText, LogOut, Lock, HardDrive, Clock, DollarSign, Sparkles, Snowflake, Brain,
} from 'lucide-react';
import { adminGet, adminPost, apiPost } from '../data/api';
import t from '../i18n/it';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };

function AdminAuth({ onAuth }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await apiPost('/admin/auth', { pin });
      sessionStorage.setItem('admin_pin', pin);
      onAuth(pin);
    } catch {
      setError(t.admin.invalid_pin);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="rounded-sm p-8 w-full max-w-sm" style={card}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-sm bg-emerald-500/10 flex items-center justify-center">
            <Shield size={18} className="text-emerald-400" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-zinc-100" style={heading}>{t.admin.auth_title}</h1>
            <p className="text-xs text-zinc-500">{t.admin.auth_subtitle}</p>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <input
            data-testid="admin-pin-input"
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder={t.admin.pin_placeholder}
            className="w-full px-4 py-3 rounded-sm text-sm text-zinc-200 mb-3 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            style={{ background: '#0D0D0F', border: '1px solid #27272A' }}
            autoFocus
          />
          {error && <div className="text-red-400 text-xs mb-3" data-testid="admin-auth-error">{error}</div>}
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading || !pin}
            className="w-full py-2.5 rounded-sm font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            style={{ background: '#10B981', color: '#000' }}
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />}
            {t.admin.login_btn}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [pin, setPin] = useState(() => sessionStorage.getItem('admin_pin') || '');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [betsapiRefreshing, setBetsapiRefreshing] = useState(false);
  const [betsapiStatus, setBetsapiStatus] = useState(null);
  const [betsapiPolling, setBetsapiPolling] = useState(false);
  const [scheduler, setScheduler] = useState(null);
  const [schedulerInterval, setSchedulerInterval] = useState(20);

  const fetchStatus = useCallback(async () => {
    if (!pin) return;
    setLoading(true);
    try {
      const data = await adminGet('/admin/system-status', pin);
      setStatus(data);
    } catch (e) {
      if (e.message === 'unauthorized') {
        sessionStorage.removeItem('admin_pin');
        setPin('');
      }
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleBetsapiRefresh = async () => {
    setBetsapiRefreshing(true);
    setBetsapiStatus(null);
    try {
      const result = await adminPost('/tt/admin/refresh-betsapi', pin);
      if (result.status === 'started' || result.status === 'already_running') {
        setBetsapiPolling(true);
        pollBetsapiStatus();
      }
    } catch (e) {
      setBetsapiStatus({ error: e.message });
      setBetsapiRefreshing(false);
    }
  };

  const pollBetsapiStatus = async () => {
    const poll = async () => {
      try {
        const data = await adminGet('/tt/admin/refresh-status', pin);
        if (!data.running && data.last_result) {
          setBetsapiStatus(data.last_result);
          setBetsapiRefreshing(false);
          setBetsapiPolling(false);
          return;
        }
      } catch { /* retry */ }
      setTimeout(poll, 5000);
    };
    poll();
  };

  const handleCleanupStale = async () => {
    try {
      const result = await adminPost('/tt/admin/cleanup-stale', pin);
      setBetsapiStatus({ status: 'cleanup', expired: result.expired_matches });
    } catch { /* handled */ }
  };

  const fetchScheduler = useCallback(async () => {
    if (!pin) return;
    try {
      const data = await adminGet('/tt/admin/scheduler', pin);
      setScheduler(data.scheduler);
      if (data.scheduler?.interval_minutes) setSchedulerInterval(data.scheduler.interval_minutes);
    } catch { /* handled */ }
  }, [pin]);

  useEffect(() => { fetchScheduler(); }, [fetchScheduler]);

  const handleSchedulerToggle = async () => {
    const newEnabled = !scheduler?.enabled;
    try {
      const data = await adminPost('/tt/admin/scheduler', pin, {
        enabled: newEnabled,
        interval_minutes: schedulerInterval,
      });
      setScheduler(data.scheduler);
    } catch { /* handled */ }
  };

  const handleSchedulerIntervalChange = async (mins) => {
    setSchedulerInterval(mins);
    if (scheduler?.enabled) {
      try {
        const data = await adminPost('/tt/admin/scheduler', pin, {
          enabled: true,
          interval_minutes: mins,
        });
        setScheduler(data.scheduler);
      } catch { /* handled */ }
    }
  };

  const logout = () => {
    sessionStorage.removeItem('admin_pin');
    setPin('');
  };

  if (!pin) return <AdminAuth onAuth={setPin} />;

  return (
    <div className="space-y-5 fade-in max-w-3xl" data-testid="admin-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-zinc-50 tracking-tight" style={heading}>{t.admin.title}</h1>
          <p className="text-zinc-400 mt-1 text-sm">{t.admin.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/llm-costs"
            data-testid="nav-llm-costs"
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium text-zinc-400 hover:text-emerald-300 transition-colors"
            style={{ border: '1px solid #27272A' }}
          >
            <DollarSign size={13} /> LLM Costs
          </Link>
          <Link
            to="/admin/feature-explorer"
            data-testid="nav-feature-explorer"
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium text-zinc-400 hover:text-emerald-300 transition-colors"
            style={{ border: '1px solid #27272A' }}
          >
            <Sparkles size={13} /> Feature Explorer
          </Link>
          <Link
            to="/admin/feature-snapshot-coverage"
            data-testid="nav-feature-snapshot-coverage"
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium text-zinc-400 hover:text-sky-300 transition-colors"
            style={{ border: '1px solid #27272A' }}
          >
            <Snowflake size={13} /> Snapshot Coverage
          </Link>
          <Link
            to="/admin/setscore-engine"
            data-testid="nav-setscore-engine"
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium text-zinc-400 hover:text-violet-300 transition-colors"
            style={{ border: '1px solid #27272A' }}
          >
            <Brain size={13} /> SetScore Engine
          </Link>
          <Link
            to="/admin/log-errori"
            data-testid="nav-error-logs"
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium text-zinc-400 hover:text-zinc-200 transition-colors"
            style={{ border: '1px solid #27272A' }}
          >
            <FileText size={13} /> {t.admin.error_logs}
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            style={{ border: '1px solid #27272A' }}
          >
            <LogOut size={12} /> {t.admin.logout}
          </button>
        </div>
      </div>

      {loading && !status ? (
        <div className="flex items-center gap-2 justify-center py-16 text-zinc-500">
          <Loader2 size={18} className="animate-spin" /> {t.common.loading}
        </div>
      ) : status && (
        <>
          {/* BetsAPI Refresh Section */}
          <div className="rounded-sm overflow-hidden" style={card}>
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #27272A' }}>
              <div className="flex items-center gap-2">
                <Activity size={14} className="text-emerald-400" />
                <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>BetsAPI - Table Tennis</h2>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="cleanup-stale-btn"
                  onClick={handleCleanupStale}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                  style={{ border: '1px solid #27272A' }}
                >
                  Cleanup Scaduti
                </button>
                <button
                  data-testid="betsapi-refresh-btn"
                  onClick={handleBetsapiRefresh}
                  disabled={betsapiRefreshing}
                  className="flex items-center gap-2 px-4 py-2 rounded-sm font-medium text-sm transition-colors disabled:opacity-50"
                  style={{ background: '#10B981', color: '#000' }}
                >
                  {betsapiRefreshing ? (
                    <><Loader2 size={14} className="animate-spin" /> Sync in corso...</>
                  ) : (
                    <><RefreshCw size={14} /> Refresh BetsAPI</>
                  )}
                </button>
              </div>
            </div>

            {betsapiRefreshing && (
              <div className="px-5 py-4 text-xs text-zinc-400 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin text-emerald-400" />
                Sync BetsAPI in corso... Match, odds, H2H e modelli in aggiornamento. Attendere ~2 minuti.
              </div>
            )}

            {betsapiStatus && betsapiStatus.status === 'ok' && betsapiStatus.result && (
              <div className="px-5 py-4 text-xs" style={{ borderTop: '1px solid #27272A' }}>
                <div className="flex items-center gap-2 text-emerald-400 font-medium mb-3">
                  <Check size={13} /> Refresh BetsAPI completato ({Math.round((betsapiStatus.result.duration_ms || 0) / 1000)}s)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-sm p-3" style={{ background: '#0D0D0F', border: '1px solid #1F1F22' }}>
                    <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Match Upcoming</div>
                    <div className="text-lg font-bold text-zinc-200 mt-1" style={mono}>{betsapiStatus.result.upcoming?.matches_stored || 0}</div>
                  </div>
                  <div className="rounded-sm p-3" style={{ background: '#0D0D0F', border: '1px solid #1F1F22' }}>
                    <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Odds Trovate</div>
                    <div className="text-lg font-bold text-zinc-200 mt-1" style={mono}>{betsapiStatus.result.odds?.odds_stored || 0}</div>
                  </div>
                  <div className="rounded-sm p-3" style={{ background: '#0D0D0F', border: '1px solid #1F1F22' }}>
                    <div className="text-zinc-500 text-[10px] uppercase tracking-wider">H2H Records</div>
                    <div className="text-lg font-bold text-zinc-200 mt-1" style={mono}>{betsapiStatus.result.h2h?.h2h_records || 0}</div>
                  </div>
                  <div className="rounded-sm p-3" style={{ background: '#0D0D0F', border: '1px solid #1F1F22' }}>
                    <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Modelli con Value</div>
                    <div className="text-lg font-bold text-emerald-400 mt-1" style={mono}>{betsapiStatus.result.models?.matches_with_value || 0}</div>
                  </div>
                </div>
                {betsapiStatus.result.models?.signals?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <div className="text-zinc-500 uppercase text-[10px] tracking-wider mb-1">Top Value Signals</div>
                    {betsapiStatus.result.models.signals.slice(0, 5).map((s, i) => (
                      <div key={i} className="flex justify-between items-center text-xs py-1" style={{ borderBottom: '1px solid #1F1F22' }}>
                        <span className="text-zinc-300">{s.match}</span>
                        <span className="text-emerald-400 font-mono">EV {s.best_ev > 0 ? '+' : ''}{s.best_ev.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {betsapiStatus && betsapiStatus.status === 'cleanup' && (
              <div className="px-5 py-4 text-xs text-zinc-400">
                <Check size={12} className="inline text-emerald-400 mr-1" />
                {betsapiStatus.expired} match scaduti rimossi
              </div>
            )}

            {betsapiStatus && betsapiStatus.error && (
              <div className="px-5 py-4 text-xs text-red-400">
                <AlertTriangle size={12} className="inline mr-1" />
                Errore: {betsapiStatus.error}
              </div>
            )}
          </div>

          {/* Auto-Refresh Scheduler */}
          <div className="rounded-sm overflow-hidden" style={card}>
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #27272A' }}>
              <div className="flex items-center gap-2">
                <Clock size={14} className="text-blue-400" />
                <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>Auto-Refresh Scheduler</h2>
              </div>
              <div className="flex items-center gap-3">
                <select
                  data-testid="scheduler-interval"
                  value={schedulerInterval}
                  onChange={(e) => handleSchedulerIntervalChange(Number(e.target.value))}
                  className="px-2 py-1 rounded-sm text-xs text-zinc-300 bg-zinc-900 border border-zinc-700 focus:outline-none"
                >
                  {[5, 10, 15, 20, 30, 45, 60].map(m => (
                    <option key={m} value={m}>{m} min</option>
                  ))}
                </select>
                <button
                  data-testid="scheduler-toggle-btn"
                  onClick={handleSchedulerToggle}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-sm font-medium text-sm transition-colors ${
                    scheduler?.enabled
                      ? 'bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25'
                      : 'bg-blue-500/15 text-blue-400 border border-blue-500/30 hover:bg-blue-500/25'
                  }`}
                >
                  {scheduler?.enabled ? 'Disattiva' : 'Attiva'}
                </button>
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="flex items-center gap-6 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${scheduler?.enabled ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-600'}`} />
                  <span className="text-zinc-400">{scheduler?.enabled ? 'Attivo' : 'Disattivato'}</span>
                </div>
                {scheduler?.enabled && scheduler?.next_run && (
                  <div className="text-zinc-500">
                    Prossimo run: <span className="text-zinc-300 font-mono">{new Date(scheduler.next_run).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                  </div>
                )}
                {scheduler?.last_auto_run && (
                  <div className="text-zinc-500">
                    Ultimo auto-run: <span className="text-zinc-300 font-mono">{new Date(scheduler.last_auto_run).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                )}
                <div className="text-zinc-500">
                  Run completati: <span className="text-zinc-300 font-mono">{scheduler?.runs_completed || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Record Counts */}
          <div className="rounded-sm overflow-hidden" style={card}>
            <div className="px-5 py-4 flex items-center gap-2" style={{ borderBottom: '1px solid #27272A' }}>
              <HardDrive size={14} className="text-zinc-400" />
              <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>{t.admin.record_counts}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4">
              {status.record_counts && Object.entries(status.record_counts).map(([key, count]) => (
                <div key={key} className="px-5 py-3" style={{ borderRight: '1px solid #27272A', borderBottom: '1px solid #27272A' }}>
                  <div className="text-xs text-zinc-500 capitalize">{key.replace(/_/g, ' ')}</div>
                  <div className="text-lg font-bold text-zinc-200 mt-1" style={mono}>{count}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Error Log Summary */}
          {status.error_log_counts && (
            <div className="flex items-center gap-4 px-1">
              <span className="text-xs text-zinc-600">Log:</span>
              <span className="flex items-center gap-1 text-xs text-red-400">
                <AlertTriangle size={11} /> {status.error_log_counts.error} {t.admin.level_error.toLowerCase()}
              </span>
              <span className="flex items-center gap-1 text-xs text-amber-400">
                <Activity size={11} /> {status.error_log_counts.warning} {t.admin.level_warning.toLowerCase()}
              </span>
              <span className="flex items-center gap-1 text-xs text-zinc-500">
                <Activity size={11} /> {status.error_log_counts.info} {t.admin.level_info.toLowerCase()}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
